import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Loader2, 
  MapPin, User, Clock, X 
} from 'lucide-react';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isSameDay, parseISO, isToday } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';
import RealBookingDetailModal from '../components/BookingDetailModal';
import AddAppointmentDialog from '../components/AddAppointmentDialog';
import QuickBlockDialog from '../components/QuickBlockDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CalendarPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 0 })
  );

  // Generate days of the week
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  useEffect(() => {
    loadBookings();
  }, [currentWeekStart]);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dashboard/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'all' }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousWeek = () => {
    setCurrentWeekStart(prev => subWeeks(prev, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(prev => addWeeks(prev, 1));
  };

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
  };

  // Get bookings for a specific day
  const getBookingsForDay = (day) => {
    return bookings.filter(booking => {
      const bookingDate = parseISO(booking.startDateTime);
      return isSameDay(bookingDate, day);
    }).sort((a, b) => {
      return new Date(a.startDateTime) - new Date(b.startDateTime);
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'bg-green-500';
      case 'PENDING': return 'bg-amber-500';
      case 'COMPLETED': return 'bg-blue-500';
      case 'CANCELED': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'CONFIRMED': return 'default';
      case 'PENDING': return 'secondary';
      case 'COMPLETED': return 'outline';
      case 'CANCELED': return 'destructive';
      default: return 'secondary';
    }
  };

  // Booking Card Component
  const BookingBlock = ({ booking }) => {
    const time = format(parseISO(booking.startDateTime), 'h:mm a');
    
    return (
      <div 
        className={`p-2 rounded-md text-xs cursor-pointer hover:opacity-80 transition-opacity border-l-4 ${getStatusColor(booking.status)} bg-card border shadow-sm`}
        onClick={() => setSelectedBooking(booking)}
        data-testid={`booking-block-${booking.id}`}
      >
        <div className="font-semibold truncate">{booking.serviceName}</div>
        <div className="text-muted-foreground truncate flex items-center gap-1 mt-0.5">
          <Clock className="h-3 w-3" />
          {time}
        </div>
        <div className="text-muted-foreground truncate flex items-center gap-1">
          <User className="h-3 w-3" />
          {booking.clientName}
        </div>
        {booking.horseCount > 0 && (
          <div className="text-muted-foreground truncate">
            {booking.horseCount} {booking.horseCount === 1 ? 'horse' : 'horses'}
          </div>
        )}
      </div>
    );
  };

  // Day Column Component
  const DayColumn = ({ day }) => {
    const dayBookings = getBookingsForDay(day);
    const isCurrentDay = isToday(day);
    
    return (
      <div 
        className={`flex flex-col min-h-[200px] ${isCurrentDay ? 'bg-primary/5' : ''}`}
        data-testid={`day-column-${format(day, 'yyyy-MM-dd')}`}
      >
        {/* Day Header */}
        <div className={`text-center py-2 border-b ${isCurrentDay ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
          <div className="text-xs font-medium">{format(day, 'EEE')}</div>
          <div className={`text-lg font-bold ${isCurrentDay ? '' : ''}`}>{format(day, 'd')}</div>
        </div>
        
        {/* Bookings */}
        <div className="flex-1 p-1 space-y-1 overflow-y-auto">
          {dayBookings.length === 0 ? (
            <div className="text-center py-4 text-xs text-muted-foreground">
              No bookings
            </div>
          ) : (
            dayBookings.map(booking => (
              <BookingBlock key={booking.id} booking={booking} />
            ))
          )}
        </div>
      </div>
    );
  };

  // Booking Detail Modal
  const BookingDetailModal = ({ booking, onClose }) => {
    if (!booking) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <CardContent className="pt-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-bold text-lg">{booking.serviceName}</h3>
                <Badge variant={getStatusBadgeVariant(booking.status)} className="mt-1">
                  {booking.status}
                </Badge>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <CalendarIcon className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Date & Time</p>
                  <p className="text-muted-foreground">
                    {format(parseISO(booking.startDateTime), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-muted-foreground">
                    {format(parseISO(booking.startDateTime), 'h:mm a')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Client</p>
                  <p className="text-muted-foreground">{booking.clientName}</p>
                  <p className="text-muted-foreground">{booking.clientPhone}</p>
                </div>
              </div>

              {booking.horseCount > 0 && (
                <div className="flex items-start gap-2">
                  <span className="text-muted-foreground mt-0.5">🐴</span>
                  <div>
                    <p className="font-medium">Horses</p>
                    <p className="text-muted-foreground">
                      {booking.horseCount} {booking.horseCount === 1 ? 'horse' : 'horses'}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <p className="font-medium">Location</p>
                  <p className="text-muted-foreground">{booking.locationAddress}</p>
                  {booking.locationNotes && (
                    <p className="text-xs text-muted-foreground mt-1">{booking.locationNotes}</p>
                  )}
                </div>
              </div>

              {booking.notes && (
                <div className="pt-2 border-t">
                  <p className="font-medium mb-1">Notes</p>
                  <p className="text-muted-foreground text-sm">{booking.notes}</p>
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose}>
                Close
              </Button>
              <Button className="flex-1" onClick={() => {
                window.location.href = `/dashboard/bookings`;
              }}>
                View All Bookings
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="calendar-title">
              <CalendarIcon className="h-6 w-6" />
              Calendar
            </h1>
            <p className="text-muted-foreground">
              {format(currentWeekStart, 'MMMM d')} - {format(addDays(currentWeekStart, 6), 'MMMM d, yyyy')}
            </p>
          </div>

          {/* Navigation + quick actions */}
          <div className="flex flex-wrap items-center gap-2">
            <AddAppointmentDialog onCreated={loadBookings} />
            <QuickBlockDialog onBlocked={loadBookings} />
            <div className="flex items-center gap-2 ml-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToPreviousWeek}
              data-testid="prev-week-button"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Prev</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
              data-testid="today-button"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goToNextWeek}
              data-testid="next-week-button"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-500"></div>
            <span>Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span>Confirmed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-500"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span>Canceled</span>
          </div>
        </div>

        {/* Calendar Grid - Desktop */}
        <div className="hidden md:grid grid-cols-7 border rounded-lg overflow-hidden" data-testid="calendar-desktop">
          {weekDays.map(day => (
            <DayColumn key={day.toISOString()} day={day} />
          ))}
        </div>

        {/* Calendar List - Mobile */}
        <div className="md:hidden space-y-4" data-testid="calendar-mobile">
          {weekDays.map(day => {
            const dayBookings = getBookingsForDay(day);
            const isCurrentDay = isToday(day);
            
            return (
              <Card 
                key={day.toISOString()} 
                className={isCurrentDay ? 'border-primary border-2' : ''}
              >
                <CardContent className="p-4">
                  <div className={`font-bold mb-3 ${isCurrentDay ? 'text-primary' : ''}`}>
                    {format(day, 'EEEE, MMMM d')}
                    {isCurrentDay && <Badge className="ml-2" variant="default">Today</Badge>}
                  </div>
                  
                  {dayBookings.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No bookings
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {dayBookings.map(booking => (
                        <div 
                          key={booking.id}
                          className={`p-3 rounded-lg border-l-4 ${getStatusColor(booking.status)} bg-muted cursor-pointer`}
                          onClick={() => setSelectedBooking(booking)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-semibold">{booking.serviceName}</p>
                              <p className="text-sm text-muted-foreground">
                                {format(parseISO(booking.startDateTime), 'h:mm a')}
                              </p>
                            </div>
                            <Badge variant={getStatusBadgeVariant(booking.status)} className="text-xs">
                              {booking.status}
                            </Badge>
                          </div>
                          <div className="mt-2 text-sm text-muted-foreground space-y-1">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {booking.clientName}
                            </div>
                            {booking.horseCount > 0 && (
                              <div>
                                {booking.horseCount} {booking.horseCount === 1 ? 'horse' : 'horses'}
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate">{booking.locationAddress}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Booking Detail Modal — use the real shared modal so providers get
            full info + Reschedule button + payment tracking + private notes */}
        {selectedBooking && (
          <RealBookingDetailModal
            bookingId={selectedBooking.id}
            open={!!selectedBooking}
            onOpenChange={(o) => !o && setSelectedBooking(null)}
            onUpdated={loadBookings}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
